import { useState } from "react";
import {
  RecordContextProvider,
  useListContext,
} from "ra-core";
import { Link } from "react-router";
import {
  AlertTriangle,
  ExternalLink,
  Flame,
  Linkedin,
  MessageSquare,
  Sparkles,
  Star,
  Target,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar } from "./Avatar";
import {
  CopyHookButton,
  GhostEnrichButton,
  JobChangeBadge,
  LeadScoreBadge,
  OutreachStageSelect,
} from "./LinkedInCrmWidgets";
import { AiSequenceBadge, AiSequenceModal } from "./AiSequenceModal";
import { ChampionStarToggle } from "./JobChangeRadarModal";
import { GroupEntityBadge } from "../misc/GroupEntitySelector";
import type { Contact } from "../types";

type QuickFilter = "all" | "champions" | "hot_icp" | "job_changes" | "hook_ready" | "active_dm";

export const ClayContactsTable = () => {
  const {
    data: contacts,
    isPending,
    selectedIds,
    onToggleItem,
  } = useListContext<Contact>();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [expandedVariantRow, setExpandedVariantRow] = useState<
    Record<string | number, number>
  >({});
  const [sequenceContact, setSequenceContact] = useState<Contact | null>(null);

  if (isPending) {
    return <Skeleton className="w-full h-64 rounded-xl" />;
  }

  const allContacts = contacts ?? [];

  const filteredContacts = allContacts.filter((c) => {
    if (quickFilter === "champions") return Boolean(c.is_champion);
    if (quickFilter === "hot_icp") return (c.lead_score ?? 0) >= 85;
    if (quickFilter === "job_changes") return Boolean(c.job_change_flagged);
    if (quickFilter === "hook_ready")
      return Boolean(c.ai_icebreaker || c.ai_dm_variants?.length);
    if (quickFilter === "active_dm")
      return c.outreach_stage === "dm_sent" || c.outreach_stage === "replied";
    return true;
  });

  const championsCount = allContacts.filter((c) => c.is_champion).length;
  const hotCount = allContacts.filter((c) => (c.lead_score ?? 0) >= 85).length;
  const jobChangeCount = allContacts.filter((c) => c.job_change_flagged).length;
  const hookReadyCount = allContacts.filter(
    (c) => c.ai_icebreaker || c.ai_dm_variants?.length,
  ).length;

  return (
    <div className="space-y-3">
      {/* Quick Signal Filter Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setQuickFilter("all")}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
              quickFilter === "all"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            All Prospects ({allContacts.length})
          </button>

          <button
            type="button"
            onClick={() => setQuickFilter("champions")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
              quickFilter === "champions"
                ? "bg-amber-500 text-amber-950 font-semibold shadow-xs"
                : "bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20"
            }`}
          >
            <Star className="size-3 fill-amber-400 text-amber-500" />
            <span>Champions</span>
            <span className="opacity-80">({championsCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setQuickFilter("hot_icp")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
              quickFilter === "hot_icp"
                ? "bg-emerald-600 text-white shadow-xs"
                : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20"
            }`}
          >
            <Flame className="size-3" />
            <span>High Intent / Hot ICP</span>
            <span className="opacity-80">({hotCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setQuickFilter("job_changes")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
              quickFilter === "job_changes"
                ? "bg-rose-600 text-white shadow-xs"
                : "bg-rose-500/10 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20"
            }`}
          >
            <AlertTriangle className="size-3" />
            <span>Job Changes</span>
            <span className="opacity-80">({jobChangeCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setQuickFilter("hook_ready")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
              quickFilter === "hook_ready"
                ? "bg-indigo-600 text-white shadow-xs"
                : "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/20"
            }`}
          >
            <Target className="size-3" />
            <span>Hook Ready</span>
            <span className="opacity-80">({hookReadyCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setQuickFilter("active_dm")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
              quickFilter === "active_dm"
                ? "bg-sky-600 text-white shadow-xs"
                : "bg-sky-500/10 text-sky-700 dark:text-sky-300 hover:bg-sky-500/20"
            }`}
          >
            <MessageSquare className="size-3" />
            <span>Active DMs</span>
          </button>
        </div>
      </div>

      {/* High-Density Clay/Attio Data Grid */}
      <div className="overflow-x-auto rounded-xl border bg-card shadow-xs">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="w-10 px-3 py-2.5"></th>
              <th className="min-w-56 px-3 py-2.5">Prospect & LinkedIn Headline</th>
              <th className="min-w-44 px-3 py-2.5">Company & Signal</th>
              <th className="w-24 px-3 py-2.5">AI Score</th>
              <th className="w-36 px-3 py-2.5">Outreach Stage</th>
              <th className="w-36 px-3 py-2.5">AI Cadence</th>
              <th className="min-w-80 px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="size-3.5 text-indigo-500" />
                  <span>AI Icebreaker & DM Angles</span>
                </div>
              </th>
              <th className="w-36 px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {filteredContacts.map((contact) => {
              const variants = Array.isArray(contact.ai_dm_variants)
                ? contact.ai_dm_variants
                : [];
              const selectedVariantIdx = expandedVariantRow[contact.id] ?? -1;
              const currentHook =
                selectedVariantIdx === -1
                  ? contact.ai_icebreaker || variants[0]?.message || ""
                  : variants[selectedVariantIdx]?.message ||
                    contact.ai_icebreaker ||
                    "";

              return (
                <RecordContextProvider key={contact.id} value={contact}>
                  <tr className="group hover:bg-muted/35 transition-colors align-top">
                    <td className="px-3 py-3">
                      <Checkbox
                        className="cursor-pointer mt-1"
                        checked={selectedIds.includes(contact.id)}
                        onCheckedChange={() => onToggleItem(contact.id)}
                      />
                    </td>

                    {/* Prospect + Headline */}
                    <td className="px-3 py-3">
                      <div className="flex items-start gap-2.5">
                        <Link to={`/contacts/${contact.id}/show`} className="shrink-0">
                          <Avatar width={25} height={25} />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <ChampionStarToggle contact={contact} />
                            <Link
                              to={`/contacts/${contact.id}/show`}
                              className="font-semibold text-foreground hover:underline truncate"
                            >
                              {contact.first_name} {contact.last_name}
                            </Link>
                            <GroupEntityBadge entityId={contact.group_entity} />
                            {contact.linkedin_url && (
                              <a
                                href={contact.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open LinkedIn Profile"
                                className="text-sky-600 hover:text-sky-500 shrink-0"
                              >
                                <Linkedin className="size-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                            {contact.headline || contact.title || "No headline yet"}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Company & Job Change Signal */}
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <div className="font-medium text-xs text-foreground">
                          {contact.company_name ||
                            contact.detected_company ||
                            "—"}
                        </div>
                        {contact.job_change_flagged ? (
                          <JobChangeBadge contact={contact} />
                        ) : contact.detected_company &&
                          contact.company_name &&
                          contact.detected_company !== contact.company_name ? (
                          <span className="text-[11px] text-muted-foreground">
                            LinkedIn: {contact.detected_company}
                          </span>
                        ) : (
                          <span className="text-[11px] text-emerald-600 dark:text-emerald-400">
                            ✓ Verified on LinkedIn
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Lead Score */}
                    <td className="px-3 py-3">
                      <LeadScoreBadge contact={contact} />
                    </td>

                    {/* Outreach Stage */}
                    <td className="px-3 py-3">
                      <OutreachStageSelect contact={contact} />
                    </td>

                    {/* AI Cadence / Sequence */}
                    <td className="px-3 py-3">
                      <AiSequenceBadge
                        contact={contact}
                        onClick={() => setSequenceContact(contact)}
                      />
                    </td>

                    {/* AI Icebreaker & DM Angles */}
                    <td className="px-3 py-3">
                      {currentHook ? (
                        <div className="space-y-1.5">
                          <p className="text-xs text-foreground/90 leading-relaxed line-clamp-2 italic">
                            "{currentHook}"
                          </p>
                          <div className="flex flex-wrap items-center gap-1">
                            {variants.length > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedVariantRow((prev) => ({
                                      ...prev,
                                      [contact.id]: -1,
                                    }))
                                  }
                                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium cursor-pointer ${
                                    selectedVariantIdx === -1
                                      ? "bg-indigo-600 text-white"
                                      : "bg-muted text-muted-foreground hover:text-foreground"
                                  }`}
                                >
                                  Hook
                                </button>
                                {variants.map((v, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() =>
                                      setExpandedVariantRow((prev) => ({
                                        ...prev,
                                        [contact.id]: i,
                                      }))
                                    }
                                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium cursor-pointer ${
                                      selectedVariantIdx === i
                                        ? "bg-indigo-600 text-white"
                                        : "bg-muted text-muted-foreground hover:text-foreground"
                                    }`}
                                  >
                                    {v.label}
                                  </button>
                                ))}
                              </>
                            )}
                            <CopyHookButton
                              icebreaker={currentHook}
                              size="sm"
                              variant="ghost"
                              label="Copy Hook"
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          Click Ghost Enrich to generate AI hook →
                        </span>
                      )}
                    </td>

                    {/* Row Actions */}
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <GhostEnrichButton
                          contact={contact}
                          size="sm"
                          variant="outline"
                        />
                        {contact.linkedin_url && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="cursor-pointer px-2"
                            title="Open LinkedIn Profile"
                            onClick={() =>
                              window.open(
                                contact.linkedin_url!,
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                          >
                            <ExternalLink className="size-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                </RecordContextProvider>
              );
            })}

            {filteredContacts.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No contacts match this signal filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {sequenceContact && (
        <AiSequenceModal
          contact={sequenceContact}
          open={Boolean(sequenceContact)}
          onOpenChange={(open) => {
            if (!open) setSequenceContact(null);
          }}
        />
      )}
    </div>
  );
};
